/**
 * Les dossiers de ce client — 9.T.4, puis 9.BI.1.
 *
 * ## Ce que la ligne dit
 *
 * La même chose que `/projects`, avec les mêmes mots : l'état opérationnel du
 * serveur (`app.project_operational`, 0082), les assureurs sollicités, les
 * réponses reçues et attendues, la prochaine échéance, le retard, et le geste
 * qui correspond. Les composants viennent de `projects/_parts/EtatOperationnel`
 * — **importés, pas réécrits** : une liste qui redéfinit un état finit par en
 * afficher un autre.
 *
 * ## Ce qu'elle ne dit plus
 *
 * `projects.status` : huit valeurs dont une seule est écrite, tous les dossiers
 * vivants à `draft`. Le badge ne se trompait pas, il ne décrivait rien (0082).
 *
 * ## La frontière du bloc
 *
 * L'entreprise oriente, le dossier exécute. Chaque geste ici *mène* au dossier ;
 * aucun n'envoie, ne relance, ni ne compare sur place.
 *
 * Pas de pagination : `limit=10` couvre la fiche, et `/projects?company_id=…`
 * donne la vue complète.
 */

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useProjects } from "../../../lib/queries/projects";
import {
  EtatOperationnelAction,
  EtatOperationnelBadge,
  EtatOperationnelDetail,
} from "../../projects/_parts/EtatOperationnel";

import { CompanyCardSection } from "./CompanyCardSection";

const LIST_LIMIT = 10;

type Props = {
  readonly companyId: string;
};

export function CompanyProjectsSection({ companyId }: Props) {
  const { t } = useTranslation();
  const q = useProjects({ company_id: companyId, limit: LIST_LIMIT });

  /**
   * 9.BI.1 — la fiche client HÉRITE, elle ne recoupe plus.
   *
   * 9.BF.1 croisait ici deux lectures — les brouillons du cabinet et les
   * échéances du client — pour deviner ce qu'un dossier attendait. C'était du
   * rangement plus que du calcul, mais c'était quand même une **seconde façon**
   * de répondre à « où en est ce dossier », dans le navigateur, à côté de celle
   * du serveur. Deux façons finissent par ne plus dire la même chose.
   *
   * `GET /v1/projects` porte l'état opérationnel depuis 9.BG.1. La ligne s'y
   * réduit donc aux mêmes champs que `/projects` : une seule définition, un seul
   * endroit où la corriger. La liste des brouillons du cabinet disparaît de la
   * page ; les échéances y restent, mais pour le bloc « ce qui presse », qui
   * répond à une autre question — laquelle de MES dossiers d'abord.
   */
  const items = q.data?.items ?? [];

  return (
    <CompanyCardSection
      title={t("companies.projects_section.title", { count: items.length })}
      // Même création que l’entrée globale, avec le client déjà choisi.
      headerAction={
        <Link
          to={`/requests/new?company_id=${companyId}`}
          className="focus-visible:ring-brand-500 bg-brand-600 hover:bg-brand-700 inline-flex min-h-11 touch-manipulation items-center rounded-lg px-3 py-1.5 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          {t("nav.new_request")}
        </Link>
      }
    >
      {q.isLoading ? (
        <p className="text-sm text-slate-500" aria-live="polite">
          {t("app.loading")}
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500">{t("companies.projects_section.empty")}</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((p) => (
            <li key={p.id} className="py-2">
              <Link
                to={`/projects/${p.id}`}
                className="focus-visible:ring-brand-500 -mx-2 block rounded px-2 py-1 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
                data-testid={`company-projects-section-item-${p.id}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-slate-900">{p.title}</div>
                    <div className="truncate">
                      {p.recipients_prepared !== undefined && (
                        <p className="text-xs text-slate-600">
                          {t("flow_clarity.prepared_count", { count: p.recipients_prepared })}
                        </p>
                      )}
                      <EtatOperationnelDetail
                        historique={p.recipients_sent_historical}
                        sollicites={
                          p.recipients_sent_current ??
                          p.recipients_sent ??
                          (p.requests_sent === 0 ? 0 : p.insurers_solicited)
                        }
                        recues={p.responses_received}
                        attente={p.responses_awaiting}
                        echeance={p.next_due_at}
                      />
                    </div>
                  </div>
                  <span
                    className="shrink-0"
                    data-testid={`company-projects-section-status-${p.id}`}
                  >
                    <EtatOperationnelBadge
                      etat={p.operational_state}
                      activityScope={p.activity_scope}
                      enRetard={p.is_late}
                    />
                  </span>
                </div>
              </Link>

              {/* Le geste utile, HORS du lien qui enveloppe la ligne : un lien
                  dans un lien n'est pas navigable au clavier. */}
              <div className="mt-1 flex justify-end">
                <EtatOperationnelAction
                  projectId={p.id}
                  etat={p.operational_state}
                  activityScope={p.activity_scope}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </CompanyCardSection>
  );
}
